import React, { useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { CreditCard, Building2, CheckCircle, Loader2 } from 'lucide-react';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const PaymentSelection = ({ bookingData, onUpdate, totalPrice, onPayPalSuccess }) => {
  const [paypalError, setPaypalError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const paymentMethods = [
    { id: 'paypal', label: 'PayPal / Card', icon: CreditCard, description: 'Pay securely with PayPal or debit/credit card' },
    { id: 'bacs', label: 'Bank Transfer', icon: Building2, description: 'Pay via bank transfer (details sent by email)' },
  ];

  const handleCreateOrder = async () => {
    setPaypalError(null);
    const response = await fetch('/api/paypal/create-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: totalPrice,
        booking_reference: bookingData.bookingReference || 'NEW'
      })
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.detail || 'Failed to create order');
    }
    const data = await response.json();
    return data.id;
  };

  const handleApprove = async (data) => {
    setProcessing(true);
    try {
      const response = await fetch('/api/paypal/capture-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: data.orderID })
      });
      if (!response.ok) {
        throw new Error('Payment capture failed');
      }
      const result = await response.json();
      if (result.status === 'COMPLETED') {
        onPayPalSuccess({
          orderId: result.order_id,
          captureId: result.capture_id,
          status: result.status
        });
      } else {
        setPaypalError('Payment was not completed. Please try again.');
      }
    } catch (err) {
      setPaypalError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-selection">
      <h3 className="text-lg font-semibold text-gray-900">Payment Method</h3>
      <p className="text-sm text-gray-600">Total: <span className="font-bold text-green-700">£{totalPrice?.toFixed(2)}</span> (no VAT to pay)</p>
      
      <div className="grid gap-3">
        {paymentMethods.map((method) => {
          const Icon = method.icon;
          const isSelected = bookingData.paymentMethod === method.id;
          return (
            <button
              key={method.id}
              type="button"
              data-testid={`payment-method-${method.id}`}
              className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-green-600 bg-green-50 shadow-sm'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
              onClick={() => {
                onUpdate({ paymentMethod: method.id });
                setPaypalError(null);
              }}
            >
              <div className={`p-2 rounded-lg ${isSelected ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{method.label}</p>
                <p className="text-sm text-gray-500">{method.description}</p>
              </div>
              {isSelected && <CheckCircle className="w-5 h-5 text-green-600" />}
            </button>
          );
        })}
      </div>

      {bookingData.paymentMethod === 'paypal' && PAYPAL_CLIENT_ID && (
        <div className="mt-6 p-4 bg-gray-50 rounded-xl" data-testid="paypal-buttons-container">
          {processing ? (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Processing payment...</span>
            </div>
          ) : (
            <PayPalScriptProvider options={{ 
              clientId: PAYPAL_CLIENT_ID, 
              currency: 'GBP',
              intent: 'capture'
            }}>
              <p className="text-sm text-gray-600 mb-3">Click below to pay securely with PayPal or debit/credit card:</p>
              <PayPalButtons
                style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                createOrder={handleCreateOrder}
                onApprove={handleApprove}
                onError={(err) => {
                  console.error('PayPal error:', err);
                  setPaypalError('Payment failed. Please try again or choose a different method.');
                }}
                onCancel={() => {
                  setPaypalError('Payment was cancelled.');
                }}
              />
            </PayPalScriptProvider>
          )}
          {paypalError && (
            <p className="mt-3 text-sm text-red-600" data-testid="paypal-error">{paypalError}</p>
          )}
        </div>
      )}

      {bookingData.paymentMethod === 'bacs' && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm text-blue-800">
            Bank transfer details will be included in your booking confirmation email. 
            Please pay within 3 working days of receiving the invoice.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentSelection;
